import React, { useState } from 'react'
import { Button } from '../../../../../../components/ui/button'
import { Loader2, Save } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../components/ui/tabs'
import { Tab, TabItem } from '../../../../../../components/ui/tab'
import { Checkbox } from '../../../../../../components/ui/checkbox'
import { Input } from '../../../../../../components/ui/input'
import NewCheckbox from '../../../../../../components/ui/new-checkbox'

import lodash from 'lodash'
import { useQuery } from '@tanstack/react-query'
import toast, { Toaster } from 'react-hot-toast'
import axios from '../../../../../../lib/axios'
import useAuth from '../../../../../../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../../redux'
import { GenericCoin } from '../../../../../../components/elements/GenericCoin'
import { GenericNote } from '../../../../../../components/elements/GenericNote'

type Props = {
  denom: PartnerDenom;
  refetch: () => void;
}

const Denom : React.FC<Props>= ({denom, refetch}) => {
  const {auth} = useAuth()
  const {t} = useTranslation()

  const [subpage, setsubpage] = useState('c');
  const [new_denom, setnew_denom] = useState<PartnerDenom>(denom)
  const coupureConfig = useSelector((state: RootState) => state.coupure)

  const visibleCoins = Array.isArray(new_denom.coins)
    ? new_denom.coins
        .map((value, index) => ({ value, index }))
        .filter(({ value }) =>
          coupureConfig.some((cfg) => cfg.order === true && cfg.cn === 'C' && cfg.value === value.coupure)
        )
    : []

  const visibleNotes = Array.isArray(new_denom.notes)
    ? new_denom.notes
        .map((value, index) => ({ value, index }))
        .filter(({ value }) =>
          coupureConfig.some((cfg) => cfg.order === true && cfg.cn === 'N' && cfg.value === value.coupure)
        )
        .sort((a, b) => b.value.coupure - a.value.coupure)
    : []

  const updatePDenomProperty = (type: 'coins' | 'notes', index: number, key: string, value: number) => {
    setnew_denom((prevDenom) => {
      const updatedDenom = { ...prevDenom };
      updatedDenom[type] = [...prevDenom[type]];
      updatedDenom[type][index] = { ...prevDenom[type][index], [key]: value };
      return updatedDenom;
    });
  };

  const {isFetching, refetch:saveUpdates} = useQuery({
    queryKey: ['update_denom'],
    queryFn: async () => {
      try {
        toast.loading(t("settings.toast_loading"), {id:'saveUpdates'})
        let obj:any = {
          token: auth.data?.token,
          ...new_denom
        }
        obj.partner = obj.partnerid
        delete obj.partnerid
        const {data} = await axios.post(`partner/denom/createorupdate`, obj)
        if(data.errorcode!==0){throw new Error(data.message)}
        toast.success(t("settings.toast_success_p2"), {id:'saveUpdates'})
        refetch()
      } catch (error) {
        console.log(error)
        toast.error(t("settings.toast_error_p4"), {id:'saveUpdates'})
      }
      return null
    },
    enabled: false
  })

  return (
    <>
    <Toaster />
    <div className='flex justify-between mb-4'>
        <Tab onValueChange={(v)=>{setsubpage(v)}} value={subpage}>
            <TabItem value='c'>{t("settings.coins")}</TabItem>
            <TabItem value='n'>{t("settings.notes")}</TabItem>
        </Tab>

        <Button onClick={()=>{saveUpdates()}} disabled={lodash.isEqual(denom, new_denom) || isFetching} className='gap-2'><Save className='w-5'/>{isFetching ? <Loader2 className='animate-spin'/> : t("settings.button_save")}</Button>
    </div>

    <div className='grid grid-cols-2 border-b text-xs py-2 mb-2 font-semibold text-neutral-600'>
      <div>{t("settings.denomination")}</div>
      <div className='grid grid-cols-3 gap-4'>
        <p>{t("settings.minimum")}</p>
        <p>{t("settings.maximum")}</p>
        <p>{t("settings.packed")}</p>
      </div>
    </div>

    {subpage === 'c' &&
    <div className='flex flex-col gap-4'>
      {visibleCoins.map(({ value, index }) => {
      return (
        <div className='grid grid-cols-2' key={index}>
          <div className='flex items-center gap-4'>
            <div><NewCheckbox checked={value.maxqty > 0 && value.perqty > 0} disabled/></div>
            <div>
              <div className='flex items-center gap-2'>
                <div className='w-10 h-10 flex justify-center items-center bg-neutral-50 rounded-sm'>
                  <GenericCoin value={value.coupure} className='w-8 h-8' />
                </div>
                <p>EUR {value.coupure}</p>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-3 gap-4'>
            <div><Input value={value.minqty} onChange={({currentTarget})=>{ if(parseFloat(currentTarget.value)>-1){(updatePDenomProperty('coins', index, 'minqty', parseFloat(currentTarget.value)))} }} type='number'/></div>
            <div><Input value={value.maxqty} onChange={({currentTarget})=>{ if(parseFloat(currentTarget.value)>-1){(updatePDenomProperty('coins', index, 'maxqty', parseFloat(currentTarget.value)))} }} type='number'/></div>
            <div><Input value={value.perqty} onChange={({currentTarget})=>{ if(parseFloat(currentTarget.value)>-1){(updatePDenomProperty('coins', index, 'perqty', parseFloat(currentTarget.value)))} }} type='number'/></div>
          </div>
        </div>
      )
    })}
    </div>
    }

    {subpage === 'n' &&
    <div className='flex flex-col gap-4'>
      {visibleNotes.map(({ value, index }) => {
      return (
        <div className='grid grid-cols-2' key={index}>
          <div className='flex items-center gap-4'>
            <div><NewCheckbox checked={value.maxqty > 0 && value.perqty > 0} disabled/></div>
            <div>
              <div className='flex items-center gap-2'>
                <div className='flex justify-center items-center w-14 h-7 bg-neutral-50 border rounded-sm'>
                  <GenericNote value={value.coupure} currency='EUR' className='w-full h-full' />
                </div>
                <p>EUR {value.coupure}</p>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-3 gap-4'>
            <div><Input value={value.minqty} onChange={({ currentTarget }) => { if(parseFloat(currentTarget.value)>-1){updatePDenomProperty('notes', index, 'minqty', parseFloat(currentTarget.value))} }} type='number' /></div>
            <div><Input value={value.maxqty} onChange={({ currentTarget }) => { if(parseFloat(currentTarget.value)>-1){updatePDenomProperty('notes', index, 'maxqty', parseFloat(currentTarget.value))} }} type='number' /></div>
            <div><Input value={value.perqty} onChange={({ currentTarget }) => { if(parseFloat(currentTarget.value)>-1){updatePDenomProperty('notes', index, 'perqty', parseFloat(currentTarget.value))} }} type='number' /></div>
          </div>
        </div>
      )
    })}
    </div>
    }

    </>
  )
}

export default Denom